/**
 * The night sky. One surface, one light source, four positions in a sunrise.
 *
 * Before this, every dark section on the page ran the same blue-left /
 * green-right wash at a different opacity. Four bands lit identically read as
 * repetition, and repetition is the thing that makes a long page feel like
 * scrolling rather than arriving.
 *
 * So `intensity` (how bright?) is replaced by `phase` (how far into the
 * morning?). 0 is the hour before dawn: a cold blue sky and one ember at the
 * horizon. 1 is sunrise: the blue has retreated upward and warm light floods
 * from below. Every dark section on the page sits at a point on that arc, in
 * order, which turns the bands from a stripe pattern into a clock.
 *
 * Two rules make it hold together:
 *
 *   1. The light always comes from the LOWER LEFT. Not top-left, not centred.
 *      A consistent, low, off-axis source is the single cheapest thing that
 *      makes a page look lit rather than tinted.
 *   2. The bright edge is the BOTTOM one — it is a horizon, the seam between
 *      this sky and the lit ground of the section beneath it.
 *
 * `tint` survives from the old system but is demoted to one job: which colour
 * the counter-light is. Verification sections keep their green whisper. It can
 * no longer restyle a whole section, because that is what made "the green one"
 * and "the blue one" look like two different sites.
 */

// The two poles of the sunrise. Everything between is interpolated, so no
// section can invent a colour that is not on this arc.
const PRE_DAWN = {
  ground: [5, 7, 15],
  sky: [34, 62, 168], skyA: 0.85,
  horizon: [96, 138, 255], horizonA: 0.44,
  ember: [255, 143, 94], emberA: 0.16,
  counterA: 0.18,
  edgeA: 0.45,
};
const SUNRISE = {
  ground: [13, 16, 36],
  sky: [58, 52, 150], skyA: 0.55,
  horizon: [255, 168, 112], horizonA: 0.5,
  ember: [255, 214, 152], emberA: 0.32,
  counterA: 0.1,
  edgeA: 0.8,
};

const COUNTER = { brand: [88, 140, 255], verified: [16, 185, 129] };

const lerp = (a, b, t) => a + (b - a) * t;
const mixRgb = (a, b, t) => a.map((v, i) => Math.round(lerp(v, b[i], t)));
const rgba = (c, a) => `rgba(${c[0]},${c[1]},${c[2]},${Number(a).toFixed(3)})`;

export default function Stage({
  phase = 0,
  tint = "brand",
  grid = true,
  className = "",
  // Children are wrapped in a positioned div so they sit above the light
  // layers — which means layout classes put on `className` land on the
  // section and never reach the children. Anything that lays the CONTENT out
  // (flex, justify-between, grid) belongs here instead.
  contentClassName = "",
  children,
  id,
}) {
  const t = Math.min(1, Math.max(0, phase));
  const ground = mixRgb(PRE_DAWN.ground, SUNRISE.ground, t);
  const sky = mixRgb(PRE_DAWN.sky, SUNRISE.sky, t);
  const horizon = mixRgb(PRE_DAWN.horizon, SUNRISE.horizon, t);
  const ember = mixRgb(PRE_DAWN.ember, SUNRISE.ember, t);
  const counter = COUNTER[tint] || COUNTER.brand;
  const at = (k) => lerp(PRE_DAWN[k], SUNRISE[k], t);

  return (
    <section
      id={id}
      data-phase={t}
      // Flips the semantic token layer to the night theme for everything
      // inside, so shadcn components on a dark section are correct without a
      // `dark ?` ternary at each call site.
      data-surface="night"
      className={`relative overflow-hidden text-white ${className}`}
      style={{ backgroundColor: `rgb(${ground.join(",")})` }}
    >
      {/* The sky and the light beneath it. One element, four layers, painted
          back to front — cheaper than four stacked divs and it keeps the
          blending predictable. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 motion-safe:animate-daybreak"
        style={{
          background: [
            // the sky, high and left
            `radial-gradient(70rem 46rem at 18% -14%, ${rgba(sky, at("skyA"))}, transparent 68%)`,
            // the light source, low and left — this is the sun
            `radial-gradient(84rem 40rem at 4% 100%, ${rgba(horizon, at("horizonA"))}, transparent 64%)`,
            // the warmth it throws, arriving only as the phase climbs
            `radial-gradient(50rem 28rem at 20% 104%, ${rgba(ember, at("emberA"))}, transparent 62%)`,
            // counter-light, so the right side has form instead of flat black
            `radial-gradient(46rem 32rem at 97% 6%, ${rgba(counter, at("counterA"))}, transparent 64%)`,
          ].join(","),
        }}
      />
      {grid && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-[0.045]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,.9) 1px, transparent 1px)," +
              "linear-gradient(90deg, rgba(255,255,255,.9) 1px, transparent 1px)",
            backgroundSize: "72px 72px",
            // Fades out toward the top so the grid belongs to the ground,
            // rather than hanging in the sky like graph paper.
            maskImage: "linear-gradient(to top, black 10%, transparent 85%)",
            WebkitMaskImage: "linear-gradient(to top, black 10%, transparent 85%)",
          }}
        />
      )}
      {/* The horizon: a lit seam at the bottom edge, glow spilling upward. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 bottom-0 h-24"
        style={{
          background: `linear-gradient(to top, ${rgba(horizon, at("edgeA") * 0.14)}, transparent)`,
        }}
      />
      <div
        aria-hidden="true"
        className="absolute inset-x-0 bottom-0 h-px"
        style={{
          background:
            `linear-gradient(90deg, ${rgba(horizon, at("edgeA"))} 0%, ` +
            `${rgba(horizon, at("edgeA") * 0.5)} 45%, transparent 85%)`,
        }}
      />
      <div className={`relative ${contentClassName}`}>{children}</div>
    </section>
  );
}
